import Spinner from "../Spinner";
import { default as _Button } from "react-bootstrap/Button";
import { PropsWithChildren, Suspense } from "react";
import { Else, If, Then, Unless, When } from "react-if";
import { t } from "@i18n";
import styled from "styled-components";
import { ButtonVariant } from "react-bootstrap/types";
import classnames from "classnames";
import { useCurrentUser } from "_api/hooks/auth";
import { ButtonIconSuspense, ButtonSuspense } from "_components/suspense/ButtonSuspense";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export type ButtonProps = PropsWithChildren & {
    type?: "button" | "submit" | "reset" | undefined;
    tooltip?: string;
    icon?: string
    text?: string;
    loading?: boolean;
    disabled?: boolean;
    className?: string;
    perm?: string[];
    onClick?: () => any | Promise<void>;
    variant?: ButtonVariant;
};

const _IconButton = styled(_Button)`
    @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
    }

    background-color: transparent !important;
    border: none;
    border-radius: 100%;
    color: inherit !important;
    padding: 0;
    font-size: 1.1rem;
    transition: transform 0.2s ease;

    &:hover {
        transform: rotate(-20deg);

        &.loading {
            transition: none;
            transform: none;
        }
    }
`;
export const Buttons = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;
`;

const RenderButton = ({
    loading,
    text,
    icon,
    children,
    onClick,
    tooltip,
    disabled,
    variant: _variant,
    perm,
    ...rest
}: PropsWithChildren<ButtonProps>) => {
    const variant = _variant || 'primary';
    const isOnlyIcon = !text && icon;

    const content = text && icon // Check if it has icon and text
        ? <>
            <i className={classnames('fas', icon, 'me-1')} />
            {text}
        </>
        : isOnlyIcon // Not text and icon, check if is only icon
            ? <i className={classnames('fas', icon, 'text-' + variant)} />
            : text // It is not only icon, check if it has text
                ? text
                : children; // No icon and no text, use children

    const Comp: React.ElementType = isOnlyIcon ? _IconButton : _Button;

    const classes = classnames(
        rest.className,
        { loading },
        { 'p-0': variant === 'link' }
    );

    const button = <Comp
        {...rest}
        className={classes}
        variant={variant}
        disabled={loading || disabled}
        onClick={() => (!loading && !disabled) && onClick && onClick()}
    >
        <When condition={loading}>
            <When condition={isOnlyIcon}>
                <Spinner className="spinner-border-sm" tag="span" />
            </When>
            <Unless condition={isOnlyIcon}>
                <Spinner className="spinner-border-sm me-1" tag="span" color="white" />
                Aguarde...
            </Unless>
        </When>
        <Unless condition={loading}>
            {content}
        </Unless>
    </Comp>;

    if (tooltip) {
        return <OverlayTrigger
            placement='top'
            overlay={
                <Tooltip id='tooltip-top'>
                    {tooltip}
                </Tooltip>
            }
        >
            {button}
        </OverlayTrigger>;
    }

    return button;
}

const RenderPermButton = ({
    perm,
    ...rest
}: PropsWithChildren<ButtonProps>) => {
    const { user } = useCurrentUser();

    if (!!perm && !user.permissions?.some(p => perm.some(_ => _ == p))) {
        return null;
    }

    return <RenderButton {...{ ...rest }} />;
}

export const Button = (props: PropsWithChildren<ButtonProps>) => {
    const isOnlyIcon = !props.text && props.icon;
    return (
        <Suspense fallback={isOnlyIcon ? <ButtonIconSuspense /> : <ButtonSuspense />}>
            <If condition={!!props.perm}>
                <Then>
                    <RenderPermButton {...{ ...props }} />
                </Then>
                <Else>
                    <RenderButton {...{ ...props }} />
                </Else>
            </If>
        </Suspense>
    );
}

export const LinkButton = (props: ButtonProps) => <Button {...props} variant='link' />;
export const CloseButton = ({ variant, ...props }: ButtonProps) => <Button icon='fa-times' text='Fechar'
    variant={variant ?? 'light'} {...props} />;

export const BackButton = (props: ButtonProps) => <Button icon='fa-arrow-left' text={t('Back')}
    variant='light' {...props} />;
export const SaveButton = ({ submit, ...props }: ButtonProps & { submit?: boolean }) => <Button
    text={t('Save')} icon='fa-floppy-disk' type={submit ? 'submit' : 'button'} {...props} />


export const SubmitButton = (props: ButtonProps) => <Button {...props} type='submit' />;
export const AddButton = (props: ButtonProps) => <Button icon='fa-plus' {...props} />;

export const IconButton = (props: ButtonProps) => <Button {...props} text={undefined} />;
export const EditIconButton = (props: ButtonProps) => <IconButton icon='fa-edit' {...props} />;
export const ViewIconButton = (props: ButtonProps) => <IconButton icon='fa-eye' {...props} />;
export const ToggleActiveIconButton = (props: ButtonProps & { active: boolean }) => {
    const { active, ...rest } = props;
    const icon = active ? 'fa-lock' : 'fa-lock-open';
    return <IconButton icon={icon} {...rest} />;
}
export const DeleteIconButton = (props: ButtonProps) => <IconButton icon='fa-trash' {...props} variant='danger' />;
